import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveUser } from '@/hooks/useEffectiveUser';
import { supabase } from '@/integrations/supabase/client';
import { format, isToday, addDays } from 'date-fns';

interface SmartStudent {
  id: string;
  name: string;
  phone?: string;
  data_vencimento?: string;
  valor_contratado?: number;
}

interface ScheduleItem {
  id: string;
  scheduled_date: string;
  scheduled_time?: string;
  status?: string;
  student_id: string;
  student?: SmartStudent | null;
}

interface SmartAction {
  id: string;
  type: 'confirm_class' | 'start_training' | 'charge_payment' | 'schedule';
  label: string;
  description: string;
  priority: number;
  data?: any;
}

export function useSmartActions() {
  const { user } = useAuth();
  const { effectiveUserId } = useEffectiveUser();
  const [todaySchedules, setTodaySchedules] = useState<ScheduleItem[]>([]);
  const [pendingPayments, setPendingPayments] = useState<SmartStudent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (effectiveUserId) {
      loadData();
    }
  }, [effectiveUserId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      
      // Load today's schedules
      const { data: schedules } = await supabase
        .from('schedule')
        .select('*')
        .eq('scheduled_date', today)
        .order('scheduled_time');

      if (schedules) {
        // Enrich with student data
        const enrichedSchedules = await Promise.all(
          schedules.map(async (schedule) => {
            if (!schedule.student_id) return { ...schedule, student: null };
            
            const { data: student } = await supabase
              .from('students')
              .select('id, name, phone, data_vencimento, valor_contratado')
              .eq('id', schedule.student_id)
              .single();
              
            return { ...schedule, student };
          })
        );
        setTodaySchedules(enrichedSchedules);
      }

      // Load students with overdue payments
      const { data: students } = await supabase
        .from('students')
        .select('id, name, phone, data_vencimento, valor_contratado')
        .not('data_vencimento', 'is', null);

      if (students) {
        const overdue = students.filter(student => {
          if (!student.data_vencimento) return false;
          const dueDate = new Date(student.data_vencimento);
          return dueDate < new Date();
        });
        setPendingPayments(overdue);
      }
    } catch (error) {
      console.error('Error loading smart actions data:', error);
    } finally {
      setLoading(false);
    }
  };

  const smartActions = useMemo(() => {
    const actions: SmartAction[] = [];
    const currentHour = new Date().getHours();

    // Morning: suggest confirming classes
    if (currentHour >= 6 && currentHour < 12) {
      const unconfirmed = todaySchedules.filter(s => s.status !== 'confirmed');
      if (unconfirmed.length > 0) {
        actions.push({
          id: 'confirm_classes',
          type: 'confirm_class',
          label: 'Confirmar aulas de hoje',
          description: `${unconfirmed.length} aula${unconfirmed.length > 1 ? 's' : ''} para confirmar`,
          priority: 9,
          data: unconfirmed,
        });
      }
    }

    // Near session time: suggest starting
    const now = new Date();
    const upcomingSession = todaySchedules.find(schedule => {
      if (!schedule.scheduled_time || schedule.status === 'completed') return false;
      
      const [hours, minutes] = schedule.scheduled_time.split(':').map(Number);
      const sessionTime = new Date();
      sessionTime.setHours(hours, minutes, 0, 0);
      
      const diffMinutes = (sessionTime.getTime() - now.getTime()) / (1000 * 60);
      return diffMinutes > -30 && diffMinutes < 60; // Within 30min before or 1h after
    });

    if (upcomingSession?.student) {
      actions.push({
        id: `start_${upcomingSession.id}`,
        type: 'start_training',
        label: `Treinar com ${upcomingSession.student.name.split(' ')[0]}`,
        description: upcomingSession.scheduled_time || 'Agora',
        priority: 10,
        data: upcomingSession,
      });
    }

    // End of month or overdue: suggest charging
    const dayOfMonth = new Date().getDate();
    if ((dayOfMonth >= 25 || dayOfMonth <= 5) && pendingPayments.length > 0) {
      actions.push({
        id: 'charge_payments',
        type: 'charge_payment',
        label: 'Cobrar pendências',
        description: `${pendingPayments.length} pagamento${pendingPayments.length > 1 ? 's' : ''} atrasado${pendingPayments.length > 1 ? 's' : ''}`,
        priority: 8,
        data: pendingPayments,
      });
    }

    // Sort by priority
    return actions.sort((a, b) => b.priority - a.priority).slice(0, 4);
  }, [todaySchedules, pendingPayments]);

  const nextSession = useMemo(() => {
    const now = new Date();
    
    const upcoming = todaySchedules
      .filter(schedule => {
        if (!schedule.scheduled_time || schedule.status === 'completed') return false;
        
        const [hours, minutes] = schedule.scheduled_time.split(':').map(Number);
        const sessionTime = new Date();
        sessionTime.setHours(hours, minutes, 0, 0);
        
        return sessionTime > now;
      })
      .sort((a, b) => {
        const timeA = a.scheduled_time || '00:00';
        const timeB = b.scheduled_time || '00:00';
        return timeA.localeCompare(timeB);
      })[0];

    if (!upcoming?.student) return null;

    return {
      studentName: upcoming.student.name,
      time: upcoming.scheduled_time,
      scheduleId: upcoming.id,
      studentId: upcoming.student.id,
    };
  }, [todaySchedules]);

  return {
    smartActions,
    nextSession,
    pendingPaymentsCount: pendingPayments.length,
    todaySessionsCount: todaySchedules.length,
    loading,
    refresh: loadData,
  };
}